import React from 'react';
import { Link } from 'react-router-dom';
import { PATENT_DEMO_LIST } from '../data/patentDemos';
import { generateAllPatentDemoPdf } from '../utils/patentPdf';

export default function PatentDemoHub() {
  return (
    <div className="min-h-screen text-white">
      {/* Hero */}
      <section className="border-b border-blue-800/30 py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <span className="inline-block text-xs font-mono px-3 py-1 bg-blue-500/10 text-blue-300 rounded-full border border-blue-500/20 mb-4">11 Patents Filed · 9 Interactive Demos · PDF Brief Exports</span>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            SALAR Patent <span className="text-cyan-300">Demo Suite</span>
          </h1>
          <p className="text-lg text-blue-200 max-w-3xl mx-auto mb-6">
            SALAR now tracks eleven patent filings. The current interactive demo suite covers the nine existing public demos, each with a public-safe explanatory narrative and SALAR-template PDF export. Patent 10 is represented through the portfolio page, news update, public brief, glossary, and Figure 1 PDF while the working demo is developed separately.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            <button
              type="button"
              onClick={() => generateAllPatentDemoPdf(PATENT_DEMO_LIST)}
              className="px-5 py-2.5 bg-cyan-500/15 border border-cyan-400/50 text-cyan-100 rounded-lg text-sm font-semibold hover:bg-cyan-500/25 transition"
            >
              Export All 9 Existing SALAR Demo Briefs
            </button>
            <Link to="/news-updates" className="px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-500 transition">
              View News Update
            </Link>
            <Link to="/patent-portfolio#patent-10" className="px-5 py-2.5 bg-cyan-600 text-white rounded-lg text-sm font-semibold hover:bg-cyan-500 transition">
              View Patent 10
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-blue-300">
            <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-teal-400" />11 patent filings</span>
            <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-cyan-400" />9 public demos</span>
            <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-violet-400" />Defense, AI, semiconductor focus</span>
          </div>
        </div>
      </section>

      {/* Explainer */}
      <section className="border-b border-blue-800/30 py-10 px-4 bg-blue-950/10">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="rounded-xl border border-cyan-500/25 bg-cyan-950/20 p-5">
            <span className="text-xs font-mono uppercase tracking-wider text-cyan-300 block mb-2">What changed</span>
            <p className="text-sm text-blue-100 leading-relaxed">The demo pages now carry the narrative that a customer needs: what the requirement is, what the patent area means, and why the method matters.</p>
          </div>
          <div className="rounded-xl border border-teal-500/25 bg-teal-950/20 p-5">
            <span className="text-xs font-mono uppercase tracking-wider text-teal-300 block mb-2">PDF output</span>
            <p className="text-sm text-blue-100 leading-relaxed">Each page exports a SALAR-template PDF brief; this hub can also export the nine current patent demo briefs into a single multi-page PDF.</p>
          </div>
          <div className="rounded-xl border border-amber-500/25 bg-amber-950/20 p-5">
            <span className="text-xs font-mono uppercase tracking-wider text-amber-300 block mb-2">Patent 10 status</span>
            <p className="text-sm text-blue-100 leading-relaxed">Patent 10 is live on the portfolio and updates pages with public-safe PDFs. A separate working demo will be added only after the interactive model is ready.</p>
          </div>
        </div>
      </section>

      {/* Patent Cards Grid */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PATENT_DEMO_LIST.map((p) => (
              <Link
                key={p.componentKey}
                to={`/patent-demos/${p.slug}`}
                className={`group relative bg-blue-950/40 rounded-xl border ${p.borderClass} p-6 hover:bg-blue-900/30 transition-all duration-200 hover:border-opacity-60 flex flex-col justify-between`}
              >
                {p.flagship && (
                  <span className="absolute -top-3 left-4 text-[10px] font-mono px-2 py-0.5 bg-amber-400 text-amber-950 rounded font-bold">★ FLAGSHIP DEMO</span>
                )}
                <div>
                  <div className="flex items-start justify-between mb-3">
                    <span className={`text-[10px] font-mono px-2 py-1 rounded border ${p.badgeBg} ${p.colorClass} ${p.borderClass}`}>{p.appNum}</span>
                    <span className="text-xs font-mono text-blue-400/60">P{p.index}</span>
                  </div>
                  <h3 className={`text-lg font-bold ${p.colorClass} mb-2 group-hover:brightness-110 transition-all`}>{p.title}</h3>
                  <p className="text-xs text-blue-200 mb-3 leading-relaxed">{p.solution}</p>
                  <p className="text-[11px] text-blue-300/70 leading-relaxed">
                    <span className="font-semibold text-blue-300">Target buyers: </span>{p.targetBuyers.split('|').join(', ')}
                  </p>
                </div>
                <div className={`mt-4 pt-3 border-t ${p.borderClass} flex items-center justify-between`}>
                  <span className={`text-xs font-semibold ${p.colorClass}`}>Launch Demo + PDF →</span>
                  <span className="text-[10px] text-blue-400/50 font-mono">Interactive</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-blue-800/30 py-14 px-4 bg-blue-950/20">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-3">Ready to license or pilot SALAR IP?</h2>
          <p className="text-blue-200 mb-6 text-sm">Start with a demo PDF export or the Patent 10 public brief. Each brief is public-safe and explains the requirement, method, good/bad case, and buyer relevance.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/licensing" className="px-6 py-2.5 bg-cyan-500/15 border border-cyan-400/40 text-cyan-100 rounded-lg text-sm font-semibold hover:bg-cyan-500/25 transition">View Licensing Options</Link>
            <Link to="/contact" className="px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-500 transition">Contact Us</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
